import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react"

import { useAgentsList } from "@/agents/catalog"
import { useAgentTransportMutations } from "@/agents/transport"
import { RegexValidationContext } from "@/metrics/registry/configuration"
import { toast } from "@coval/ui"

import { TRACKING_EVENTS, useAnalytics } from "@/lib/analytics"
import type { MetricRecordData } from "@/types/metrics/contracts"

import { getMetricType, METRIC_TYPE } from "../../metric-display"
import { useMetricPersistenceTransport } from "../../persistence"
import { useMetricDirectory } from "../../registry"
import type { MetricRecord } from "../../runtime"
import { useRegexSaveValidation } from "../hooks/useRegexSaveValidation"
import { DEFAULT_TOLERANCE_SECONDS } from "../test-metric-drawer/utils/segmentAgreement"
import type { StagedChain } from "../types/metricChains"
import type { MetricFlowsTransportState } from "../types/metricFlowsTransport"
import type {
  MetricThresholdTransportState,
  StagedThreshold,
} from "../types/metricThreshold"
import { mergeRefreshedMetric } from "../utils/mergeRefreshedMetric"
import { getUnsupportedStagedTriggerChainError } from "../utils/metricChainState"
import { getMetricSaveErrorMessage } from "../utils/metricSaveError"
import { getMetricSaveValidationError } from "../utils/metricSaveValidation"

const MAX_PROMPT_CHARACTERS = 20000

function hasSameIds(left: Set<string>, right: Set<string>): boolean {
  return left.size === right.size && [...left].every((id) => right.has(id))
}

function toastCannotSaveMetric(description: string) {
  toast.danger({
    title: "Cannot save metric",
    description,
  })
}

interface MetricViewerContextType {
  metric: MetricRecord | null
  metricRecordsById: Record<string, MetricRecord>
  isLoading: boolean
  isError: boolean
  isSaving: boolean
  isDuplicating: boolean
  isDeleting: boolean
  isNewMetric: boolean
  isBuiltIn: boolean
  updateMetric: (updates: Partial<MetricRecordData>) => void
  saveMetric: () => Promise<void>
  saveAsNewMetric: () => Promise<string | null>
  deleteMetric: (metricId: string) => Promise<void>
  hasUnsavedChanges: boolean
  selectedAgentIds: Set<string>
  setSelectedAgentIds: (ids: Set<string>) => void
  promptValidationError: string | null
  /** Non-null when a required field is missing or the metric is otherwise invalid; disables the Create/Save button. */
  saveValidationError: string | null
  toleranceSeconds: number
  setToleranceSeconds: (value: number) => void
  includeOldVersions: boolean
  setIncludeOldVersions: (value: boolean) => void
  criteriaViewMode: "score" | "criteria"
  setCriteriaViewMode: (value: "score" | "criteria") => void
  stagedChains: StagedChain[]
  addStagedChain: (chain: Omit<StagedChain, "local_id">) => void
  updateStagedChain: (
    localId: string,
    patch: Partial<Omit<StagedChain, "local_id">>,
  ) => void
  removeStagedChain: (localId: string) => void
  stagedThreshold: StagedThreshold | null
  setStagedThreshold: (threshold: StagedThreshold | null) => void
  thresholdTransport: MetricThresholdTransportState
  metricFlowsTransport: MetricFlowsTransportState
}

const MetricViewerContext = createContext<MetricViewerContextType | undefined>(
  undefined,
)

interface MetricViewerProviderProps {
  children: ReactNode
  initialMetric: MetricRecordData
  createMetricInstance: (data: MetricRecordData) => MetricRecord
  metricRecordsById: Record<string, MetricRecord>
  isNewMetric?: boolean
  isBuiltIn?: boolean
  onCreated?: (metricId: string) => void
  templateId?: string
  templateName?: string
  thresholdTransport: MetricThresholdTransportState
  metricFlowsTransport: MetricFlowsTransportState
}

export const MetricViewerProvider: React.FC<MetricViewerProviderProps> = ({
  children,
  initialMetric,
  createMetricInstance,
  metricRecordsById,
  isNewMetric: isNewMetricProp = false,
  isBuiltIn: isBuiltInProp = false,
  onCreated,
  templateId,
  templateName,
  thresholdTransport,
  metricFlowsTransport,
}) => {
  const { trackEvent } = useAnalytics()
  const [rawMetric, setRawMetric] = useState<MetricRecordData>(initialMetric)
  const { regexValidationError, recordRegexError } =
    useRegexSaveValidation(rawMetric)
  const lastAppliedInitialMetric = useRef(initialMetric)
  // Counts local metric edits, so a save can tell whether the user kept editing while it was in flight.
  const metricEditCount = useRef(0)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(isNewMetricProp)
  const [isNewMetric, setIsNewMetric] = useState(isNewMetricProp)
  const [isBuiltIn, setIsBuiltIn] = useState(isBuiltInProp)
  const [selectedAgentIds, setSelectedAgentIds] = useState<Set<string>>(
    new Set<string>(),
  )
  const [toleranceSeconds, setToleranceSeconds] = useState(
    DEFAULT_TOLERANCE_SECONDS,
  )
  const [includeOldVersions, setIncludeOldVersions] = useState(false)
  const [criteriaViewMode, setCriteriaViewMode] = useState<
    "score" | "criteria"
  >("criteria")
  const [initialAgentIds, setInitialAgentIds] = useState<Set<string>>(
    new Set<string>(),
  )
  const [stagedChains, setStagedChains] = useState<StagedChain[]>([])
  const [stagedThreshold, setStagedThresholdRaw] =
    useState<StagedThreshold | null>(null)
  const metricPersistence = useMetricPersistenceTransport()
  const { byManager } = useMetricDirectory()

  // Merge local edits on a refresh of the same metric, but start a fresh editor when navigation changes resource identity.
  useEffect(() => {
    const previousInitialMetric = lastAppliedInitialMetric.current
    if (isNewMetric || initialMetric === previousInitialMetric) {
      return
    }

    lastAppliedInitialMetric.current = initialMetric
    setIsBuiltIn(isBuiltInProp)
    if (initialMetric.id !== previousInitialMetric.id) {
      setRawMetric(initialMetric)
      setHasUnsavedChanges(false)
      setSelectedAgentIds(new Set())
      setInitialAgentIds(new Set())
      setStagedChains([])
      setStagedThresholdRaw(null)
      return
    }
    setRawMetric((localMetric) =>
      hasUnsavedChanges
        ? mergeRefreshedMetric(
            previousInitialMetric,
            localMetric,
            initialMetric,
          )
        : initialMetric,
    )
  }, [hasUnsavedChanges, initialMetric, isBuiltInProp, isNewMetric])

  // Create metricInstance from metric data
  const metric = useMemo(() => {
    return rawMetric ? createMetricInstance(rawMetric) : null
  }, [createMetricInstance, rawMetric])

  const promptLength = metric?.metric_metadata?.question?.length || 0
  const promptValidationError =
    promptLength > MAX_PROMPT_CHARACTERS
      ? `Prompt exceeds ${MAX_PROMPT_CHARACTERS.toLocaleString()} character limit (${promptLength.toLocaleString()} characters)`
      : null

  // Full save-blocking validation (prompt length, numeric ranges, staged chain
  // triggers, and every required field). Drives the disabled state of the
  // Create/Save button so it stays locked until the metric is valid.
  const saveValidationError = useMemo(
    () =>
      regexValidationError ??
      (metric
        ? (getMetricSaveValidationError(metric, promptValidationError) ??
          getUnsupportedStagedTriggerChainError(stagedChains, metric))
        : null),
    [metric, promptValidationError, regexValidationError, stagedChains],
  )

  const {
    addAgentMetrics,
    isAddingAgentMetrics,
    isUpdatingAgent,
    removeAgentMetric,
  } = useAgentTransportMutations()
  const addStagedChain = useCallback((chain: Omit<StagedChain, "local_id">) => {
    setStagedChains((prev) => [
      ...prev,
      { ...chain, local_id: crypto.randomUUID() },
    ])
    setHasUnsavedChanges(true)
  }, [])

  const updateStagedChain = useCallback(
    (localId: string, patch: Partial<Omit<StagedChain, "local_id">>) => {
      setStagedChains((prev) =>
        prev.map((c) => (c.local_id === localId ? { ...c, ...patch } : c)),
      )
      setHasUnsavedChanges(true)
    },
    [],
  )

  const removeStagedChain = useCallback((localId: string) => {
    setStagedChains((prev) => prev.filter((c) => c.local_id !== localId))
    setHasUnsavedChanges(true)
  }, [])

  const setStagedThreshold = useCallback(
    (threshold: StagedThreshold | null) => {
      setStagedThresholdRaw((prev) => {
        if (prev === threshold) {
          return prev
        }
        if (threshold !== null) {
          setHasUnsavedChanges(true)
        }
        return threshold
      })
    },
    [],
  )

  // Fetch agents to determine initial associations
  const { data: agents } = useAgentsList()

  // Initialize selectedAgentIds and initialAgentIds from agents list
  useEffect(() => {
    const metricId = metric?.id
    if (agents?.length && metricId) {
      const associatedIds = agents
        .filter(
          (agent) =>
            Array.isArray(agent.metric_ids) &&
            agent.metric_ids.includes(metricId),
        )
        .map((agent) => agent.id)

      const idsSet = new Set(associatedIds)
      setSelectedAgentIds((current) =>
        hasSameIds(current, idsSet) ? current : idsSet,
      )
      setInitialAgentIds((current) =>
        hasSameIds(current, idsSet) ? current : idsSet,
      )
    }
  }, [agents, metric?.id])

  const updateMetric = useCallback((updates: Partial<MetricRecordData>) => {
    metricEditCount.current += 1
    setRawMetric((prev) => ({ ...prev, ...updates }))
    setHasUnsavedChanges(true)
  }, [])

  const handleSetSelectedAgentIds = useCallback((ids: Set<string>) => {
    setSelectedAgentIds(ids)
    setHasUnsavedChanges(true)
  }, [])

  const saveMetric = useCallback(async () => {
    if (!metric) {
      throw new Error("No metric to save")
    }

    if (metric.metric_manager === "manager_python") {
      throw new Error(
        "Revise Python metrics with Sofia and publish a tested draft.",
      )
    }

    const validationError =
      regexValidationError ??
      getMetricSaveValidationError(metric, promptValidationError) ??
      getUnsupportedStagedTriggerChainError(stagedChains, metric)
    if (validationError) {
      toastCannotSaveMetric(validationError)
      return
    }

    try {
      if (isNewMetric) {
        // metric_name is now auto-generated server-side using the pattern
        // {metric_manager}_{org_slug}_{id} to avoid race conditions
        const result = await metricPersistence.createMetric({
          metric_display_name: metric.metric_display_name.trim(),
          metric_metadata: metric.metric_metadata,
          ...(Array.isArray(metric.dependency_metric_ids)
            ? { dependency_metric_ids: metric.dependency_metric_ids }
            : {}),
          description: metric.description,
          metric_manager: metric.metric_manager,
          category: metric.category || null,
          output_type: metric.output_type,
        })

        if (result.id) {
          trackEvent(TRACKING_EVENTS.METRICS.CREATED, {
            metric_id: result.id,
            template_id: templateId || null,
            template_name: templateName || null,
          })

          // Handle agent associations for the new metric
          if (selectedAgentIds.size > 0) {
            await Promise.all(
              Array.from(selectedAgentIds).map((agentId) =>
                addAgentMetrics({
                  agentId,
                  metricIds: [result.id],
                }),
              ),
            )
          }

          // Create any staged chains with the freshly minted metric id. Without
          // organization_id none of them are sent, so all of them have failed.
          const chainOrgId = result.organization_id
          let failedChainCount = chainOrgId ? 0 : stagedChains.length
          if (stagedChains.length > 0 && chainOrgId) {
            const chainResults = await Promise.allSettled(
              stagedChains.map((sc) =>
                metricFlowsTransport.createMetricFlow({
                  trigger_metric_id:
                    sc.role === "trigger" ? result.id : sc.other_metric_id,
                  followup_metric_id:
                    sc.role === "followup" ? result.id : sc.other_metric_id,
                  trigger_criteria: sc.trigger_criteria,
                  status: sc.status,
                }),
              ),
            )
            // A fulfilled response without metric_flow was not persisted.
            failedChainCount = chainResults.filter(
              (chainResult) =>
                chainResult.status !== "fulfilled" ||
                !chainResult.value?.metric_flow,
            ).length
            setStagedChains([])
          }
          if (failedChainCount > 0) {
            toast.warning({
              title: `Metric saved, but ${failedChainCount} chain(s) failed to create`,
              description:
                "Open the metric and re-create the failed chains manually.",
            })
          }

          const metricType = getMetricType(
            metric,
            byManager[metric.metric_manager],
          )
          const supportsThreshold =
            metricType !== METRIC_TYPE.CATEGORICAL &&
            metricType !== METRIC_TYPE.SET &&
            metricType !== METRIC_TYPE.UNTYPED

          let thresholdSaveFailed = false
          if (stagedThreshold && supportsThreshold) {
            try {
              await thresholdTransport.updateThreshold(result.id, {
                comparison_operator: stagedThreshold.comparison_operator,
                target_float_upper: stagedThreshold.target_float_upper,
                target_float_lower: stagedThreshold.target_float_lower,
                target_values: stagedThreshold.target_values,
              })
              setStagedThresholdRaw(null)
            } catch {
              thresholdSaveFailed = true
              setHasUnsavedChanges(true)
              toast.warning({
                title: "Metric saved, but threshold failed to create",
                description: "Open the metric and set the threshold manually.",
              })
            }
          }

          // Notify parent and navigate to the new metric
          if (onCreated) {
            onCreated(result.id)
          }

          if (!thresholdSaveFailed) {
            setHasUnsavedChanges(false)
          }

          // A partial failure has already reported itself as a warning; a second
          // success toast would contradict it.
          if (!thresholdSaveFailed && failedChainCount === 0) {
            toast.success({ title: "Metric created" })
          }
        }

        setIsNewMetric(false)
        setIsBuiltIn(false)
        return
      }

      // Handle update mode
      if (!metric.id) {
        throw new Error("Cannot save metric without ID")
      }

      // Compute agent association diffs
      const agentsToAdd: string[] = []
      const agentsToRemove: string[] = []

      // Check for agents to add (in selectedAgentIds but not in initialAgentIds)
      selectedAgentIds.forEach((agentId) => {
        if (!initialAgentIds.has(agentId)) {
          agentsToAdd.push(agentId)
        }
      })

      // Check for agents to remove (in initialAgentIds but not in selectedAgentIds)
      initialAgentIds.forEach((agentId) => {
        if (!selectedAgentIds.has(agentId)) {
          agentsToRemove.push(agentId)
        }
      })

      // Save metric changes
      const editCountAtSave = metricEditCount.current
      const savedDisplayName = metric.metric_display_name.trim()
      await metricPersistence.updateMetric({
        id: metric.id,
        metric_name: metric.metric_name,
        metric_display_name: savedDisplayName,
        metric_metadata: metric.metric_metadata,
        ...(Array.isArray(metric.dependency_metric_ids)
          ? { dependency_metric_ids: metric.dependency_metric_ids }
          : {}),
        description: metric.description,
        metric_manager: metric.metric_manager,
        category: metric.category || null,
        output_type: metric.output_type,
      })

      // Save agent association changes
      if (agentsToAdd.length > 0) {
        await Promise.all(
          agentsToAdd.map((agentId) =>
            addAgentMetrics({
              agentId,
              metricIds: [metric.id],
            }),
          ),
        )
      }

      if (agentsToRemove.length > 0) {
        await Promise.all(
          agentsToRemove.map((agentId) =>
            removeAgentMetric({
              agentId,
              metricId: metric.id,
            }),
          ),
        )
      }

      trackEvent(TRACKING_EVENTS.METRICS.SAVED, {
        metric_id: metric.id,
      })

      // Update the initial agent IDs to match the current selection
      setInitialAgentIds(new Set(selectedAgentIds))
      // Edits made while the save was in flight were not sent, so they stay unsaved.
      if (metricEditCount.current === editCountAtSave) {
        setRawMetric((current) => ({
          ...current,
          metric_display_name: savedDisplayName,
        }))
        setHasUnsavedChanges(false)
      }
      toast.success({ title: "Metric saved" })
    } catch (error) {
      if (recordRegexError(error)) {
        return
      }
      toast.danger({
        title: "Error saving metric",
        description: getMetricSaveErrorMessage(error),
      })
      throw error
    }
  }, [
    metric,
    isNewMetric,
    metricPersistence,
    selectedAgentIds,
    initialAgentIds,
    addAgentMetrics,
    removeAgentMetric,
    onCreated,
    promptValidationError,
    regexValidationError,
    recordRegexError,
    trackEvent,
    templateId,
    templateName,
    stagedChains,
    metricFlowsTransport,
    stagedThreshold,
    thresholdTransport,
    byManager,
  ])

  const saveAsNewMetric = useCallback(async (): Promise<string | null> => {
    if (!metric) {
      throw new Error("No metric to save")
    }

    if (metric.metric_manager === "manager_python") {
      throw new Error(
        "Revise Python metrics with Sofia and publish a tested draft.",
      )
    }

    const validationError =
      regexValidationError ??
      getMetricSaveValidationError(metric, promptValidationError)
    if (validationError) {
      toastCannotSaveMetric(validationError)
      return null
    }

    try {
      const result = await metricPersistence.duplicateMetric(metric.id)

      if (result.id) {
        if (selectedAgentIds.size > 0) {
          await Promise.all(
            Array.from(selectedAgentIds).map((agentId) =>
              addAgentMetrics({
                agentId,
                metricIds: [result.id],
              }),
            ),
          )
        }

        setHasUnsavedChanges(false)
        setIsNewMetric(false)
        setIsBuiltIn(false)
        toast.success({ title: "Metric duplicated" })
        return result.id
      }

      return null
    } catch (error) {
      if (recordRegexError(error)) {
        return null
      }
      toast.danger({
        title: "Error saving metric",
        description: getMetricSaveErrorMessage(error),
      })
      throw error
    }
  }, [
    metric,
    metricPersistence,
    selectedAgentIds,
    addAgentMetrics,
    promptValidationError,
    regexValidationError,
    recordRegexError,
  ])

  const deleteMetric = useCallback(
    async (metricId: string) => {
      await metricPersistence.archiveMetric({ metric_id: metricId })
    },
    [metricPersistence],
  )

  const value = useMemo(
    () => ({
      metric,
      metricRecordsById,
      isLoading: false,
      isError: false,
      isSaving:
        metricPersistence.isUpdating ||
        metricPersistence.isCreating ||
        isAddingAgentMetrics ||
        isUpdatingAgent ||
        metricFlowsTransport.isCreating ||
        thresholdTransport.isSaving,
      isDuplicating: metricPersistence.isDuplicating,
      isDeleting: metricPersistence.isArchiving,
      isNewMetric,
      isBuiltIn,
      updateMetric,
      saveMetric,
      saveAsNewMetric,
      deleteMetric,
      hasUnsavedChanges,
      selectedAgentIds,
      setSelectedAgentIds: handleSetSelectedAgentIds,
      promptValidationError,
      saveValidationError,
      toleranceSeconds,
      setToleranceSeconds,
      includeOldVersions,
      setIncludeOldVersions,
      criteriaViewMode,
      setCriteriaViewMode,
      stagedChains,
      addStagedChain,
      updateStagedChain,
      removeStagedChain,
      stagedThreshold,
      setStagedThreshold,
      thresholdTransport,
      metricFlowsTransport,
    }),
    [
      metric,
      metricRecordsById,
      isNewMetric,
      isBuiltIn,
      updateMetric,
      saveMetric,
      saveAsNewMetric,
      hasUnsavedChanges,
      metricPersistence.isUpdating,
      promptValidationError,
      saveValidationError,
      metricPersistence.isCreating,
      metricPersistence.isDuplicating,
      metricPersistence.isArchiving,
      deleteMetric,
      isAddingAgentMetrics,
      isUpdatingAgent,
      metricFlowsTransport,
      selectedAgentIds,
      handleSetSelectedAgentIds,
      toleranceSeconds,
      includeOldVersions,
      criteriaViewMode,
      stagedChains,
      addStagedChain,
      updateStagedChain,
      removeStagedChain,
      stagedThreshold,
      setStagedThreshold,
      thresholdTransport,
    ],
  )

  return (
    <MetricViewerContext.Provider value={value}>
      <RegexValidationContext.Provider value={regexValidationError}>
        {children}
      </RegexValidationContext.Provider>
    </MetricViewerContext.Provider>
  )
}

export const useMetricViewer = (): MetricViewerContextType => {
  const context = useContext(MetricViewerContext)
  if (!context) {
    throw new Error("useMetricViewer must be used within MetricViewerProvider")
  }
  return context
}

export const useOptionalMetricViewer = ():
  | MetricViewerContextType
  | undefined => useContext(MetricViewerContext)
